/**
 * Switching the look.
 *
 * Four swatches rather than a dropdown: the whole point is what it looks like,
 * and a list of words makes you pick blind. Each swatch is painted in its own
 * theme's colours, so the control is its own preview.
 *
 * It changes immediately and it is remembered on the device, like sound and
 * vibration — this belongs to the phone, not to a game or to a household.
 */

import { THEMES, THEME_LABELS, useSettings, type Theme } from '@/store/settings';

/**
 * The swatch colours, spelled out.
 *
 * Not read from the CSS variables, because those describe *the current theme* —
 * a swatch has to show a theme that is not active. Kept next to the variables
 * in `index.css`; if one moves, move the other.
 */
const SWATCH: Record<Theme, { bg: string; card: string; primary: string; accent: string }> = {
  speels: { bg: '#16213e', card: '#1e2d4f', primary: '#6ec96e', accent: '#f4863f' },
  nu: { bg: '#0b1220', card: '#131c2e', primary: '#22c55e', accent: '#4ade80' },
  arcade: { bg: '#06010f', card: '#0f0524', primary: '#ffe600', accent: '#00f0ff' },
  kaartkamer: { bg: '#10261a', card: '#16351f', primary: '#d4af37', accent: '#e3c463' },
  poly: { bg: '#12182b', card: '#1b243e', primary: '#2dd4bf', accent: '#5eead4' },
  pixel: { bg: '#1a1033', card: '#281a4a', primary: '#5ccc6a', accent: '#7ee787' },
};

export default function ThemePicker() {
  const theme = useSettings((state) => state.theme);
  const setTheme = useSettings((state) => state.setTheme);

  return (
    <div>
      <p className="mb-2 text-sm text-slate-400">Uiterlijk</p>
      {/* Three across, two rows: six across is unreadable on a 390px phone. */}
      <ul className="grid grid-cols-3 gap-2">
        {THEMES.map((option) => {
          const swatch = SWATCH[option];
          const chosen = option === theme;
          return (
            <li key={option}>
              <button
                type="button"
                aria-pressed={chosen}
                onClick={() => setTheme(option)}
                className="w-full text-left"
              >
                <span
                  className="block overflow-hidden rounded-xl border-2 transition"
                  style={{
                    backgroundColor: swatch.bg,
                    borderColor: chosen ? swatch.primary : 'rgb(255 255 255 / 0.1)',
                  }}
                >
                  {/* A tiny mock of the thing it will change: a panel, a
                      primary button, an accent. */}
                  <span className="block px-1.5 py-2">
                    <span
                      className="mb-1 block h-2 w-2/3 rounded-full"
                      style={{ backgroundColor: swatch.accent }}
                    />
                    <span
                      className="block h-5 w-full rounded"
                      style={{ backgroundColor: swatch.card }}
                    >
                      <span
                        className="mx-1 mt-1 block h-3 rounded-sm"
                        style={{ backgroundColor: swatch.primary }}
                      />
                    </span>
                  </span>
                </span>
                <span
                  // Wrapping, not clipping: the pixel font is wide enough to
                  // turn "Kaartkamer" into "Kaartka…", which names nothing.
                  className={`mt-1 block text-center text-xs leading-tight ${
                    chosen ? 'font-medium text-white' : 'text-slate-500'
                  }`}
                >
                  {THEME_LABELS[option].name}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="mt-1 text-center text-xs text-slate-500">{THEME_LABELS[theme].hint}</p>
    </div>
  );
}
